import React, { useMemo, useState, useEffect } from "react";
import { Search as SearchIcon } from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

/* ----------------- Load doctors map ----------------- */
async function loadDoctorsMap() {
  const snap = await getDocs(collection(db, "doctors"));
  const map = {};
  snap.forEach((doc) => {
    map[doc.id] = doc.data(); // contains photoURL, name, specialty...
  });
  return map;
}

/* ----------------- Helpers ----------------- */
const toDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d?.toDate === "function") return d.toDate();
  const parsed = new Date(d);
  return isNaN(parsed) ? null : parsed;
};

const formatDate = (d) =>
  d?.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }) || "";

/* ----------------------------------------------------- */

export default function MedicalRecordsSection({
  records = [],
  setSelectedRecord,
}) {
  const [search, setSearch] = useState("");
  const [doctorMap, setDoctorMap] = useState({});
  const [enriched, setEnriched] = useState([]);

  /* ----------------- Load doctors ONCE ----------------- */
  useEffect(() => {
    loadDoctorsMap().then(setDoctorMap);
  }, []);

  /* ----------------- Merge doctors → records ----------------- */
  useEffect(() => {
    if (!records || records.length === 0) return;

    const merged = records.map((r) => {
      const docInfo = doctorMap[r.doctorId] || {};

      return {
        ...r,
        doctorInfo: {
          ...(r.doctorInfo || {}),
          name: docInfo.name || r.doctorInfo?.name || "Unknown Doctor",
          specialty:
            docInfo.specialty || r.doctorInfo?.specialty || "Unknown Specialty",
          photoURL: docInfo.photoURL || null,
        },
      };
    });

    setEnriched(merged);
  }, [records, doctorMap]);

  const searchL = search.trim().toLowerCase();

  /* ----------------- FILTER ----------------- */
  const filtered = useMemo(() => {
    if (!searchL) return enriched;
    return enriched.filter((r) => {
      const hay = [
        r?.diagnosis,
        r?.notes,
        r?.symptoms,
        r?.doctorInfo?.name,
        r?.doctorInfo?.specialty,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(searchL);
    });
  }, [enriched, searchL]);

  /* ----------------- GROUP BY MONTH ----------------- */
  const groups = useMemo(() => {
    const map = new Map();

    filtered.forEach((r) => {
      const d = toDate(r.createdAt || r.appointmentDate) || new Date();
      const label = `${d.toLocaleString("en-US", { month: "long" })} ${d.getFullYear()}`;
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(r);
    });

    return Array.from(map.entries()).sort((a, b) => {
      const da = toDate(a[1][0].createdAt)?.getTime() || 0;
      const db = toDate(b[1][0].createdAt)?.getTime() || 0;
      return db - da;
    });
  }, [filtered]);

  /* ------------------------------------------------- */

  return (
    <div style={{ width: "100%", paddingBottom: 32 }}>
      {/* ---- Title ---- */}
      <h2
        style={{
          fontSize: 20,
          fontWeight: 700,
          margin: "0 0 16px 0",
          color: "#111",
        }}
      >
        Medical Records
      </h2>

      {/* ---- Search ---- */}
      <div style={{ position: "relative", marginBottom: 20 }}>
        <SearchIcon
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            width: 18,
            height: 18,
            color: "#999",
          }}
        />

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search records…"
          style={{
            width: "100%",
            padding: "10px 12px 10px 38px",
            fontSize: 15,
            border: "1px solid #d1d5db",
            borderRadius: 8,
          }}
        />
      </div>

      {/* ---- Empty states ---- */}
      {enriched.length === 0 && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#666" }}>
          No medical records yet.
        </div>
      )}

      {enriched.length > 0 && filtered.length === 0 && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#666" }}>
          No results found.
        </div>
      )}

      {/* ---- GROUP LIST ---- */}
      {groups.map(([month, items]) => (
        <div key={month} style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#6b7280",
              padding: "6px 0",
            }}
          >
            {month}
          </div>

          {items.map((r, i) => {
            const dt = toDate(r.createdAt || r.appointmentDate);

            return (
              <div
                key={r.id || i}
                onClick={() => setSelectedRecord && setSelectedRecord(r)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "12px 0",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                }}
              >
                {/* --- Doctor Photo --- */}
                {r.doctorInfo?.photoURL ? (
                  <img
                    src={r.doctorInfo.photoURL}
                    alt=""
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background: "#e5e7eb",
                      flexShrink: 0,
                    }}
                  />
                )}

                {/* --- Text --- */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111",
                    }}
                  >
                    Dr. {r.doctorInfo?.name}
                  </div>

                  <div
                    style={{
                      fontSize: 13,
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    {r.doctorInfo?.specialty} • {formatDate(dt)}
                  </div>

                  {r.diagnosis && (
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 14,
                        color: "#374151",
                        lineHeight: 1.4,
                      }}
                    >
                      {r.diagnosis.length > 120
                        ? r.diagnosis.substring(0, 120) + "…"
                        : r.diagnosis}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
